import { useMsal } from "@azure/msal-react";
import {
  getTheme,
  IBreadcrumbProps,
  IShimmerProps,
  IStyle,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Shimmer,
  ShimmerElementType,
  Spinner,
  SpinnerSize,
  Stack,
  TooltipHost
} from "@fluentui/react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Card, Divider, IconButton, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../BasicComponents/DisplayAPIsExceptionAlert";
import { ICustomer } from "../ServiceModels/CustomerService-Models";
import { CustomBreadcrumb } from "../SharedComponents/CustomBreadcrumb";
import ShareLinkContextMenu from "../SharedComponents/ShareLinkContextMenu";
import { copy, isValidJson } from "../Utils/js-common";
import CustomerContact from "../components/CustomerView/CustomerContact";
// License component hidden for now — will be enabled in the next deployment.
// import BaselineSecurityHealthLicenseColumn from "../components/CustomerView/BaselineSecurityHealthLicenseColumn";
import CustomerDetails from "../components/CustomerView/CustomerDetails";
import CustomerInfo from "../components/CustomerView/CustomerInfo";
import { CustomerPivot } from "../components/CustomerView/CustomerPivot";
import {
  DOWNLOAD_REPORT_ERROR_MESSAGE,
  DOWNLOAD_REPORT_TIMEOUT,
  RECENTLY_VIEWED_LIMIT,
  VIEWED_KEY
} from "../components/CustomerView/CustomerView.constants";
import { logEvent, logException, logPageView } from "../helper/AppInsightsHelper";
import { getCustomerInitials } from "../helper/CustomerInitials";
import {
  useGetFavorableCustomerByLoginQuery,
  useGetTenantDataQuery,
  useGetTpidDescriptionAndLogoQuery,
  useInsertRecentlyViewedCustomerMutation,
  useInsertUpdateFavCustomersMutation
} from "../slices/ExploreCustomers.slice";

const bgColor = "#e6faff";
const customerInfoDetailsColor = "#1a1b19";
const shimmerColor = "rgb(222, 236, 249)";
const commonColor: string = "hsl(206, 100%, 42%)";
const styles = mergeStyleSets({
  customerInfoContainer: { margin: 10, minWidth: 600 } as IStyle,
  customerInfoIdentity: {
    paddingBlockEnd: 10,
    paddingInlineStart: 10,
    paddingInlineEnd: 10,
    fontSize: 14,
    display: "flex",
    justifyContent: "space-between",
    color: customerInfoDetailsColor
  } as IStyle,
  customerInfoDetails: {
    display: "flex",
    color: customerInfoDetailsColor
  } as IStyle,
  customerDetails: {
    border: "solid 0.7px #ececec",
    marginBottom: "10px",
    borderRadius: 4,
    boxShadow: "-1px 2px 1px -1.5px rgba(0,0,0,0.2)"
  } as IStyle,
  customerInfoIdentityInitialLetterStack: {
    fontSize: 44,
    color: commonColor,
    paddingTop: 8,
    border: "0.7px",
    borderRadius: 10
  } as IStyle,
  customerInfoIdentityInitialLetterStackLabel: {
    background: bgColor
  } as IStyle,
  customerInfoLabel: {
    fontWeight: "600",
    fontSize: "16px"
  } as IStyle,
  infoSpace: {
    paddingBottom: "8px"
  } as IStyle
});

const customerInfoIdentityFirstLetterShimmer = {
  style: { marginTop: 10, width: 60 },
  shimmerElements: [{ type: ShimmerElementType.gap, height: 50 }]
};
const customerInfoIdentityDetailsShimmer = {
  style: {
    marginTop: 10,
    width: 300,
    paddingLeft: 4,
    paddingRight: 3
  },
  shimmerElements: [{ type: ShimmerElementType.line }]
};
const customerInfoIdentityTenantIdShimmer = {
  style: {
    marginTop: 10,
    width: 200,
    paddingLeft: 4,
    paddingRight: 3
  },
  shimmerElements: [{ type: ShimmerElementType.line, height: 10 }]
};
const customerInfoIdentityTopPIDShimmer = {
  style: {
    marginTop: 10,
    width: 100,
    paddingLeft: 4,
    paddingRight: 3
  },
  shimmerElements: [{ type: ShimmerElementType.line, height: 8 }]
};

export default function CustomerPage() {
  const { instance, accounts } = useMsal();
  const [params] = useSearchParams();
  const tenantId = params.get("id");
  const [customer, setCustomer] = useState<ICustomer>({} as ICustomer);
  const userName = sessionStorage.getItem("loginName")?.toString();
  const userEmail = sessionStorage.getItem("loginUserName")?.toString();
  // eslint-disable-next-line no-undef
  const enviroment = process.env.REACT_APP_ENVIRONMENT;
  const navigate = useNavigate();
  const [hideInfo, setHideInfo] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const customerDetailsRef = useRef<any>();
  const { data, error, isFetching } = useGetTenantDataQuery(tenantId, {
    refetchOnMountOrArgChange: true
  });
  const { data: likeData } = useGetFavorableCustomerByLoginQuery({});
  const [updateFavoriteCustomer] = useInsertUpdateFavCustomersMutation();
  const { data: tpidDescriptionAndLogoData, isFetching: isFetchingDescriptionAndLogo } =
    useGetTpidDescriptionAndLogoQuery(
      {
        tpid: customer.TopPId || "",
        tenantId: tenantId || "",
        tenantName: customer.name || ""
      },
      {
        skip: !customer.TopPId || !tenantId || !customer.name
      }
    );
  const [logo, setLogo] = useState<string | null>(null);
  const [tenantDescription, setTenantDescription] = useState<string>("");
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  const [insertRecentlyViewedCustomer] = useInsertRecentlyViewedCustomerMutation();

  useEffect(() => {
    if (tpidDescriptionAndLogoData) {
      setLogo(tpidDescriptionAndLogoData.logoInBase64Format ?? null);
      setTenantDescription(tpidDescriptionAndLogoData.description ?? "");
    }
  }, [tpidDescriptionAndLogoData]);

  useEffect(() => {
    if (!params.get("id")?.trim()) {
      navigate("/404");
      return;
    }

    if (data) {
      setCustomer(
        (prev) =>
          ({
            ...prev,
            data: data[0],
            name: data[0]?.tenantName,
            accountManager: data[0]?.accountManager,
            accountManagerAlias: data[0]?.accountManagerAlias,
            TopPId: data[0]?.tpid,
            CustomerName: data[0]?.customerName || ""
          }) as ICustomer
      );
    }

    const customPropsForLogging = {
      CustomerName: customer?.name,
      TenantId: tenantId
    };

    logPageView("Customer Page", window.location.href, customPropsForLogging);
  }, [navigate, params, data]);

  useEffect(() => {
    if (likeData) {
      const isFavorite = Boolean(likeData.filter((f: any) => f.tenantId == tenantId).length);
      setCustomer((prev) => ({ ...prev, isFavorite: isFavorite }));
    }
  }, [enviroment, userEmail, userName, likeData, tenantId]);

  const updateFavIcon = (isFavorite: boolean) => {
    setCustomer((prev) => ({
      ...prev,
      data: { ...prev.data, IsFavorite: isFavorite },
      isFavorite: isFavorite
    }));
    updateFavoriteCustomer({
      loginName: userName,
      loginEmail: userEmail,
      tenantId: tenantId ?? "",
      isFavourite: Number(isFavorite)
    });
  };
  const hideMe = (value: any) => {
    setHideInfo(value);
  };

  useEffect(() => {
    if (customer.name) {
      // This code stores recently viewed customer Id's in session storage, maintaining a list upt 10 unique entries.
      // New customers are added to the top, duplicates are avoided, and each new entry is also sent to the API.
      const viewedKey = VIEWED_KEY;
      const checkExisting = sessionStorage.getItem(viewedKey);
      const MAX_RECENTLY_VIEWED = RECENTLY_VIEWED_LIMIT;
      let viewedList: string[] = [];
      try {
        viewedList = checkExisting && isValidJson(checkExisting) ? JSON.parse(checkExisting) : [];
      } catch (e) {
        console.error(
          "Failed to parse recently viewed customers from sessionStorage.Expected a JSON array of strings.",
          e
        );
      }
      if (tenantId && !viewedList?.includes(tenantId)) {
        viewedList.unshift(tenantId);
        if (viewedList.length > MAX_RECENTLY_VIEWED) {
          viewedList.pop();
        }
        try {
          sessionStorage.setItem(viewedKey, JSON.stringify(viewedList));
        } catch (e) {
          console.error("Failed to save recently viewed customers to sessionStorage", e);
        }
        insertRecentlyViewedCustomer({
          LoginName: userName ?? "",
          LoginEmail: userEmail ?? "",
          TenantId: tenantId,
          TenantName: customer.name
        });
      }
    }
  }, [customer.name]);

  const handleDownload = async () => {
    logEvent("Download Customer Report button was clicked.", "Customer Page", "CustomerPage", {
      tenantId,
      tenantName: customer.name
    });
    setIsExecuting(true);

    try {
      // Acquire fresh token before making the API call to avoid 401 errors
      const activeAccount = accounts[0];
      if (!activeAccount) {
        throw new Error("No active account found. Please login again.");
      }

      const tokenResponse = await instance.acquireTokenSilent({
        // eslint-disable-next-line no-undef
        scopes: JSON.parse(process.env.REACT_APP_ONECLICK_SCOPES || "[\"api://2079fc28-e367-4281-93dc-d1ea808f082d/access_as_user\"]"),
        account: activeAccount
      });

      const freshToken = tokenResponse.accessToken;
      // eslint-disable-next-line no-undef
      const oneClickApiUrl = process.env.REACT_APP_ONECLICK_API_URL || "https://fn-oneclickcx-ppe.azurewebsites.net/api";
      const url = `${oneClickApiUrl}/oneclickcx_report_generation?tenantId=${tenantId}&view_name=executive`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${freshToken}`
        }
      });

      logEvent("Report Generation Status", "Customer Page", "OneClickCustomerReport", { status: response.status });

      if (!response.ok) {
        logException("Report Generation Failed", "Customer Page", "OneClickCustomerReport", {
          status: response.status
        });
        throw new Error("Failed to fetch file");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      // eslint-disable-next-line no-useless-escape
      const sanitizedCustomerName = customer.name.replace(/[\/\\:]/g, "_");
      a.download = `${sanitizedCustomerName}_AI_Summary_Report.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setShowErrorMessage(true);
      setTimeout(() => {
        setShowErrorMessage(false);
      }, DOWNLOAD_REPORT_TIMEOUT);
      logException("Customer View", "Customer report", "oneclickcx_report_generation", error, customer.name);
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <>
      <div className={styles.customerInfoContainer}>
        <HeaderSection name={customer.name} />
        {error && (
          <DisplayAPIsException error={error} height={Math.max(customerDetailsRef.current?.offsetHeight, 200)} />
        )}
        {!error && (
          <div className={styles.customerInfoIdentity}>
            <CustomerInfoIdentity
              loading={isFetching}
              name={customer.name}
              favIcon={customer.isFavorite}
              updateFavIcon={updateFavIcon}
              tenantId={tenantId}
              TopPId={customer.TopPId}
              CustomerName={customer.CustomerName}
              error={error}
              logo={logo}
            />
            <div style={{ position: "relative", marginTop: 8 }}>
              <TooltipHost
                content="AI-powered framework designed to generate insights across all pillars."
                styles={{ root: { paddingTop: 8 } }}
              >
                <PrimaryButton
                  onClick={handleDownload}
                  style={{ height: "30px" }}
                  iconProps={{ iconName: "TextDocument" }}
                  disabled={showErrorMessage}
                >
                  {isExecuting ? (
                    <>
                      <span style={{ marginRight: 8, fontWeight: "bold" }}>Downloading...</span>
                      <Spinner size={SpinnerSize.xSmall} />
                    </>
                  ) : (
                    "Download Customer Report"
                  )}
                </PrimaryButton>
                {showErrorMessage && (
                  <div style={{ color: "red", marginTop: 8, position: "absolute" }}>
                    {DOWNLOAD_REPORT_ERROR_MESSAGE}
                  </div>
                )}
              </TooltipHost>
            </div>
          </div>
        )}
      </div>
      <div className={styles.customerInfoContainer}>
        {!error && (
          <div className={styles.customerDetails} ref={customerDetailsRef}>
            <div className={styles.infoSpace}>
              {hideInfo ? (
                <KeyboardArrowDownIcon
                  titleAccess="Click To Show Customer Information"
                  sx={{
                    color: commonColor,
                    fontSize: "2rem"
                  }}
                  style={{
                    cursor: "pointer",
                    marginBottom: "-10px"
                  }}
                  onClick={() => hideMe(false)}
                />
              ) : (
                <KeyboardArrowUpIcon
                  titleAccess="Click To Hide Customer Information"
                  sx={{
                    color: commonColor,
                    fontSize: "2rem"
                  }}
                  style={{
                    cursor: "pointer",
                    marginBottom: "-10px"
                  }}
                  onClick={() => hideMe(true)}
                />
              )}
              <span className={styles.customerInfoLabel}> Customer Information</span>
            </div>

            {!hideInfo ? (
              <div className={styles.customerInfoDetails}>
                <Stack horizontal styles={{ root: { flex: 1, minHeight: 168 } }}>
                  <Stack horizontal styles={{ root: { width: "calc(35% + 0.8px)" } }}>
                    <CustomerInfo
                      name={customer.name}
                      loaded={
                        !isFetchingDescriptionAndLogo &&
                        (!!tenantDescription || tpidDescriptionAndLogoData !== undefined)
                      }
                      displayext={tenantDescription || "No description available"}
                    />
                    <Divider orientation="vertical" style={{ marginBottom: "20px" }} flexItem />
                  </Stack>
                  <Stack horizontal styles={{ root: { flex: 1 } }}>
                    <CustomerDetails custDetails={customer.data} loading={isFetching} />
                    <Divider orientation="vertical" flexItem style={{ marginBottom: "20px" }} />
                    {/* License component hidden for now — will be enabled in the next deployment.
                    <BaselineSecurityHealthLicenseColumn tenantId={tenantId ?? undefined} loading={isFetching} />
                    <Divider orientation="vertical" flexItem style={{ marginBottom: "20px" }} />
                    */}
                    <CustomerContact
                      accountmgr={customer.accountManager}
                      accountManagerAlias={customer.accountManagerAlias}
                      loading={isFetching}
                      tenantName={customer.name}
                      tpid={customer.TopPId}
                      tenantId={tenantId}
                    />
                  </Stack>
                </Stack>
              </div>
            ) : null}
          </div>
        )}
        <Card>
          <CustomerPivot tenantName={customer?.name} topParentId={customer?.TopPId} />
        </Card>
      </div>
    </>
  );
}

// Mapping of source identifiers to display names and routes
const SOURCE_PAGE_CONFIG: Record<string, { label: string; route: string }> = {
  explorehealth: { label: "Explore Health", route: "/explorehealth" },
  customersearch: { label: "Explore Customers", route: "/customersearch" },
  products: { label: "Explore Products", route: "/products" },
  productview: { label: "Product View", route: "/productview" },
  partnersearch: { label: "Explore Partners", route: "/partnersearch" },
  partners: { label: "Partner View", route: "/partners" },
  myfavorites: { label: "My Favorites", route: "/myfavorites" },
  buildwithcustomer: { label: "Build With Customer", route: "/buildwithcustomer" },
  closetheloop: { label: "Close The Loop", route: "/closetheloop" },
  home: { label: "Home", route: "/" },
  search: { label: "Search", route: "/" }
};

const BackToSourceButton = ({ source, navigate }: { source: string | null; navigate: (path: string) => void }) => {
  if (!source || !SOURCE_PAGE_CONFIG[source]) return null;

  const config = SOURCE_PAGE_CONFIG[source];

  return (
    <div style={{ marginTop: 2, paddingLeft: 15 }}>
      <span
        onClick={() => navigate(config.route)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          padding: "3px 6px",
          backgroundColor: "#E8F4FD",
          border: "1px solid #0078D4",
          borderRadius: 10,
          color: "#0078D4",
          fontSize: 11,
          fontWeight: 500,
          cursor: "pointer",
          whiteSpace: "nowrap"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#CCE4F7";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#E8F4FD";
        }}
      >
        ← {config.label}
      </span>
    </div>
  );
};

const HeaderSection = (props: { name: string }) => {
  const theme = getTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Check if URL contains 'mf' or 'tcf' parameters
  const hasFilters = searchParams.has("mf") || searchParams.has("tcf");
  // Get the source page from URL parameter
  const fromSource = searchParams.get("from");
  const bcrops: IBreadcrumbProps = {
    items: [
      { text: "Home", key: "home", onClick: () => navigate("/") },
      { text: "Explore Customers", key: "explore-customers", onClick: () => navigate("/customersearch") },
      { text: props.name, key: "customer-name", isCurrentItem: true }
    ],
    styles: breadcrumbStyles
  };

  return (
    <Stack styles={{ root: { paddingRight: 10 } }}>
      <Stack horizontal styles={{ root: { justifyContent: "space-between", alignItems: "center" } }}>
        <div style={{ flexGrow: 1 }}>
          <CustomBreadcrumb {...bcrops} key={key} />
        </div>
        <Stack horizontal styles={{ root: { justifyContent: "space-between", alignItems: "center", columnGap: "10px" } }}>
          {hasFilters && (
            <MessageBar
              messageBarType={MessageBarType.warning}
              messageBarIconProps={{ iconName: "Filter" }}
              styles={{
                root: { borderRadius: 4, border: `1px solid ${theme.palette.orange}` },
                icon: { color: theme.palette.orange },
                text: { color: "#663c00" }
              }}
            >
              Filters are applied on this page!
            </MessageBar>
          )}
          <ShareLinkContextMenu pageType="Customer" entityName={props.name} />
        </Stack>
      </Stack>
      <BackToSourceButton source={fromSource} navigate={navigate} />
    </Stack>
  );
};

const LoadShimmer = (props: { shimmerProps: IShimmerProps; loading: boolean }) => {
  return props.loading ? <Shimmer {...props.shimmerProps} shimmerColors={{ shimmer: shimmerColor }}></Shimmer> : "";
};

const CustomerInfoIdentity = (props: {
  loading: boolean;
  name: string;
  favIcon: boolean;
  updateFavIcon: (status: boolean) => void;
  tenantId: string | null;
  TopPId: string;
  CustomerName: string;
  error: any;
  logo: string | null;
}) => {
  const custInfoIdentityRef = useRef<any>();
  const style: { display: string } = {
    display: props.error ? "block" : "flex"
  };

  return (
    <div ref={custInfoIdentityRef}>
      <Stack horizontal style={style}>
        {(!props.error && (
          <>
            <LoadShimmer shimmerProps={customerInfoIdentityFirstLetterShimmer} loading={props.loading} />
            <CustomerInfoIdentityInitialLetter {...props} tpid={props.TopPId} />
            <CustomerInfoIdentityDetails
              loading={props.loading}
              name={props.name}
              favIcon={props.favIcon}
              updateFavIcon={props.updateFavIcon}
              tenantId={props.tenantId}
              TopPId={props.TopPId}
              CustomerName={props.CustomerName}
            />
          </>
        )) || <DisplayAPIsException error={props.error} height={custInfoIdentityRef.current?.offsetHeight} />}
      </Stack>
    </div>
  );
};

const CustomerInfoIdentityDetails = (props: {
  loading: boolean;
  name: string;
  favIcon: boolean;
  updateFavIcon: (status: boolean) => void;
  tenantId: string | null;
  TopPId: string;
  CustomerName: string;
}) => {
  const [title, setTitle] = useState("Copy Tenant Id");
  const [isEnabledCopyIcon, setIsEnabledCopyIcon] = useState(false);
  const styles = mergeStyleSets({
    CustomerInfoIdentityDetailsStack: {
      display: "initial",
      paddingLeft: 10
    } as IStyle,
    CustomerInfoIdentityDetailsStackLabel: {
      fontSize: 22,
      fontWeight: "25px",
      fontSizeAdjust: "initial"
    } as IStyle,
    CustomerInfoTenantIdFlexContainer: {
      display: "inline-flex",
      alignItems: "center",
      border: isEnabledCopyIcon ? "1px solid" : "1px solid transparent",
      padding: "2px 2px 2px 0px"
    } as IStyle,
    FontSize: {
      fontSize: 12
    },
    CustomerInfoTenantIdTooltipContainer: {
      visibility: isEnabledCopyIcon ? "visible" : "hidden",
      position: "sticky"
    },
    CustomerInfoTenantIdTooltipContainerBtn: { color: "#000 !important" }
  });
  const onMouseEnterTenantId = () => {
    setIsEnabledCopyIcon(true);
    setTitle("Copy Tenant Id");
  };
  const onMouseLeaveTenantId = () => setIsEnabledCopyIcon(false);
  const copyValue = async () => {
    await copy(props.tenantId as string).then(() => {
      setTitle("Copied");
    });
  };

  return (
    <Stack className={styles.CustomerInfoIdentityDetailsStack}>
      <LoadShimmer shimmerProps={customerInfoIdentityDetailsShimmer} loading={props.loading} />
      {!props.loading && (
        <>
          <label className={styles.CustomerInfoIdentityDetailsStackLabel}>{props.name}&nbsp;</label>
          <LoadFavoriteIcon {...props} />
        </>
      )}

      <LoadShimmer shimmerProps={customerInfoIdentityTenantIdShimmer} loading={props.loading} />
      {!props.loading && (
        <>
          <br />
          <div
            className={styles.CustomerInfoTenantIdFlexContainer}
            onMouseEnter={onMouseEnterTenantId}
            onMouseLeave={onMouseLeaveTenantId}
          >
            <label className={styles.FontSize}>Tenant ID : {props.tenantId}</label>
            <div className={styles.CustomerInfoTenantIdTooltipContainer}>
              <Tooltip title={title} leaveDelay={500} arrow>
                <IconButton
                  size="small"
                  onClick={copyValue}
                  onMouseEnter={onMouseEnterTenantId}
                  className={styles.CustomerInfoTenantIdTooltipContainerBtn}
                >
                  <ContentCopyIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </>
      )}

      <LoadShimmer shimmerProps={customerInfoIdentityTopPIDShimmer} loading={props.loading} />
      {!props.loading && (
        <>
          <br />
          <label style={{ fontSize: 12 }}>
            TPID : {props.TopPId} | Customer Name : {props.CustomerName}
          </label>
        </>
      )}
    </Stack>
  );
};

const LoadFavoriteIcon = (props: { favIcon: boolean; updateFavIcon: (status: boolean) => void }) => {
  return (
    <Stack style={{ display: "inline-flex" }}>
      {props.favIcon ? (
        <FavoriteIcon
          titleAccess="Click to Un-Favorite this customer"
          sx={{
            color: commonColor,
            fontSize: "1.2rem"
          }}
          style={{ cursor: "pointer" }}
          onClick={() => props.updateFavIcon(false)}
        />
      ) : (
        <FavoriteBorderIcon
          titleAccess="Click to Favorite this customer"
          sx={{
            color: commonColor,
            fontSize: "1.2rem"
          }}
          style={{ cursor: "pointer" }}
          onClick={() => props.updateFavIcon(true)}
        />
      )}
    </Stack>
  );
};

const CustomerInfoIdentityInitialLetter = (props: {
  name: string;
  loading: boolean;
  tpid: string;
  logo: string | null;
}) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  return !props.loading ? (
    <Stack className={styles.customerInfoIdentityInitialLetterStack}>
      <label
        className={styles.customerInfoIdentityInitialLetterStackLabel}
        style={isImageLoaded ? { padding: 0 } : { padding: "0 6px" }}
      >
        <b>
          {props.logo ? (
            <img
              src={`data:image/png;base64,${props.logo}`}
              alt="Customer Logo"
              style={{
                width: "70px",
                height: "70px",
                display: "block",
                objectFit: "contain"
              }}
              onLoad={() => setIsImageLoaded(true)}
            />
          ) : (
            <>{getCustomerInitials(props.name)}</>
          )}
        </b>
      </label>
    </Stack>
  ) : (
    ""
  );
};

const key = "customer-page-bc";

const breadcrumbStyles = {
  root: {
    fontSize: "14px",
    margin: 0,
    paddingLeft: 8,
    "--itemLink-color": "rgb(0, 120, 212)"
  },
  item: {
    fontSize: "inherit"
  },
  itemLink: {
    fontSize: "inherit",
    color: "var(--itemLink-color)",
    ":hover": {
      background: "none",
      textDecoration: "underline",
      color: "var(--itemLink-color)"
    }
  }
};
